import React from "react";
import cn from "classnames";
import _ from "lodash";
import styles from "./Input.module.scss";
import { debounces } from "../constants";
import { Paragraph, InfoCaption, InfoParagraph } from "./Typography";
import { FlexBox, ClickableBox } from "./Boxes";
import {
  isCorrectUpdateForNumericInput,
  toLocaleSeparatorNumber,
  comasToDots,
} from "../utils/input";

export function InputGroup(props) {
  return <div className="inputGroup">{props.children}</div>;
}

export const InputBase = ({ value, onChange, type, renderInput }) => {
  const handleChange = (e) => onChange(e.target.value);
  return renderInput({
    value,
    type,
    onChange: handleChange,
  });
};

export const Input = (props) => (
  <InputBase
    {...props}
    renderInput={(inputProps) => (
      <input
        className={styles.input}
        type={inputProps.type}
        value={inputProps.value}
        onChange={inputProps.onChange}
        placeholder={props.placeholder}
        required={props.required}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        name={props.name}
        maxLength={props.maxLength}
        accept={props.accept}
      />
    )}
  />
);

export const InputWithLabel = (props) => (
  <InputBase
    {...props}
    renderInput={(inputProps) => (
      <>
        <Paragraph className={styles.inputLabel}>{props.label}</Paragraph>
        <input
          className={cn(styles.input, styles.inputWithLabel)}
          type={inputProps.type}
          value={inputProps.value}
          onChange={inputProps.onChange}
          placeholder={props.placeholder}
          required={props.required}
          maxLength={props.maxLength}
          accept={props.accept}
        />
      </>
    )}
  />
);

export const DebouncedInput = ({ onChange, value }) => (
  <Input value={value} onChange={_.debounce(onChange, debounces.input)} />
);

export const GreyInvertedBigInput = (props) => (
  <InputBase
    {...props}
    renderInput={(inputProps) => (
      <input
        className={styles.greyInvertedBigInput}
        type={inputProps.type}
        value={inputProps.value}
        onChange={inputProps.onChange}
        placeholder={props.placeholder}
        required={props.required}
      />
    )}
  />
);

export const DebouncedGreyInvertedBigInput = ({
  onChange,
  value,
  ...props
}) => (
  <GreyInvertedBigInput
    {...props}
    value={value}
    onChange={_.debounce(onChange, debounces.input)}
  />
);

export const Textarea = (props) => (
  <textarea
    value={props.children}
    onChange={(e) => props.onChange && props.onChange(e.target.value)}
    onBlur={(e) => props.onBlur && props.onBlur(e.target.value)}
    placeholder={props.placeholder}
    className={cn(styles.textarea, props.className)}
    rows={props.rows}
    maxLength={props.maxLength}
  />
);

export const GreyTextarea = (props) => (
  <Textarea {...props} className={cn(styles.greyTextarea, props.className)} />
);

export const TextareaWithLabel = (props) => (
  <>
    <Paragraph
      className={styles.inputLabel}
      style={{ paddingBottom: props.paddingBelow }}
    >
      {props.label}
    </Paragraph>
    <Textarea className={styles.textareaWithLabel} {...props} />
  </>
);

export const GreyTextareaWithLabel = (props) => (
  <>
    <InfoParagraph className={styles.inputLabel}>{props.label}</InfoParagraph>
    <GreyTextarea className={styles.textareaWithLabel} {...props} />
  </>
);

export default Input;

export const IncrementDecrementInput = ({
  onIncrement,
  onDecrement,
  value,
}) => (
  <FlexBox className={styles.incrementDecrementContainer}>
    <ClickableBox onClick={onIncrement}>
      <InfoParagraph>+</InfoParagraph>
    </ClickableBox>
    <InfoCaption className={styles.incrementDecrementInput}>
      {value}
    </InfoCaption>
    <ClickableBox onClick={onDecrement}>
      <InfoParagraph>-</InfoParagraph>
    </ClickableBox>
  </FlexBox>
);

export const PercentInputLabel = (props) => (
  <InputBase
    {...props}
    renderInput={(inputProps) => (
      <>
        <Paragraph className={styles.inputLabel}>{props.label}</Paragraph>
        <Paragraph className={styles.inputLabelText}>
          {props.value ? `${toLocaleSeparatorNumber(props.value)}%` : ""}
        </Paragraph>
        {props.disabled ? (
          <input
            className={cn(
              styles.input,
              styles.inputWithLabel,
              styles.textareaWithLabelForPercent,
            )}
            disabled={"disabled"}
            type={inputProps.type}
            value={inputProps.value}
            placeholder={props.placeholder}
          />
        ) : (
          <input
            className={cn(
              styles.input,
              styles.inputWithLabel,
              styles.textareaWithLabelForPercent,
            )}
            type={inputProps.type}
            value={inputProps.value}
            onChange={inputProps.onChange}
            placeholder={props.placeholder}
            required={props.required}
          />
        )}
      </>
    )}
  />
);

export const PercentInputWithLabel = (props) => {
  const handleChange = (value) => {
    if (
      isCorrectUpdateForNumericInput(value, {
        maximum: props.maximum,
        minimum: props.minimum,
      })
    )
      props.onChange(comasToDots(value));
  };
  return <PercentInputLabel {...props} onChange={handleChange} />;
};

PercentInputWithLabel.defaultProps = {
  minimum: 0,
  maximum: 100,
};
